import { useRoute, useRouter } from 'vue-router';
import {
  useImageBufferCache,
  useTextFont,
  useThemeManage,
  useWatchfaceHistory,
  useWatchfaceInit,
  useWatchfaceTypeChange
} from '.';
import { onBeforeMount, onMounted, watch } from 'vue';
import { useEditorStore } from '@/store/use-editor-store';
import { useLayerStore } from '@/store/use-layer-store';
import { useRuntimeStore } from '@/store/use-runtime-store';
import { useEmitterListener } from './use-event-listener';
import { emitter, EventType } from '@/utils/emitter.utils';
import { Shape, ShapeType } from '@/services/shape';
import { cloneDeep, debounce } from 'lodash-es';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import ShapeAttribute, { ResourceKey } from '@/services/shape/attributes';
import { WatchfaceService } from '@/services/watchface';
import { ElectronApi } from '@/services/electron.api';
import { Modal } from 'ant-design-vue';
import showToast from '@/components/common/toast';

export const useEditorInit = () => {
  const { initWatchfaceData } = useWatchfaceInit();
  const { addItem, updateItem } = useWatchfaceHistory();
  const { addNormalTheme, addThemeColor } = useThemeManage();
  const layerStore = useLayerStore();
  const watchfaceStore = useWatchfaceStore();
  const editorStore = useEditorStore();

  // 初始化表盘数据
  const route = useRoute();
  const router = useRouter();
  watch(
    () => route.query,
    async () => {
      if (route.name == 'editor') await _init();
    },
    { deep: true }
  );

  const _init = async () => {
    const { path } = route.query ?? {};
    if (path) {
      const res = await initWatchfaceData(path.toString());
      if (!res) return router.push('/home');

      // 选择默认的theme
      editorStore.selectTheme();
    } else {
      console.log('未提供path，无法初始化数据');
    }
    if (!layerStore.themes?.length) {
      addNormalTheme();
    }
  };
  onMounted(async () => {
    await _init();
  });

  onBeforeMount(() => {
    // 清理旧数据
    editorStore.$reset();
  });

  // 当选择一个样式时
  useEmitterListener(EventType['theme:selected'], () => {
    // 默认选择一个配色
    if (editorStore.theme && !editorStore.themeColor) {
      addThemeColor(editorStore.theme, '#ffffff');
    }
    // 加载该theme的字体
    // if (editorStore.theme) loadThemeFonts(editorStore.theme.id);
    // 选择theme时，删除当前选择
    editorStore.hovered = '';
    editorStore.unselect();
  });

  // 当选择一个themeColor时
  // useEmitterListener(EventType['theme-color:selected'], () => {});
  watch(
    [
      () => editorStore.themeColorId,
      () => watchfaceStore.description?.watchfaceType
    ],
    () => {
      // 判断是都需要禁用右侧面板属性
      const index = editorStore.theme?.colorGroupTable?.findIndex(
        (item) => item.id == editorStore.themeColorId
      );

      editorStore.attrDisabled =
        index == 0
          ? false
          : watchfaceStore.colorMode == 'recolor'
          ? 'all'
          : 'not-image';
    }
  );

  // 监听shape属性修改
  useEmitterListener(
    EventType['shape:set-attr'],
    (data?: { shapeId: string; attrs: (keyof ShapeAttribute['attrs'])[] }) => {
      const shapeId = data?.shapeId;
      const shape0 = Shape.getShape(data?.shapeId ?? '');
      if (!shapeId || !shape0) return;

      // 修改当前active的shape属性
      editorStore.activeShapes.forEach((shape) => {
        const _id = shape?.id;
        if (!_id) return;
        if (_id == shapeId) {
          shape.attrs = cloneDeep(shape0?.attrs ?? {});
        }
        // 如果还修改的slot下子元素
        if (_id == shape0.parent?.id) {
          shape.attrs = cloneDeep(shape0.parent?.attrs ?? {});
        }
      });
    }
  );

  // 修复资源时，默认为其他配色修改资源
  useEmitterListener(
    EventType['shape:upload-resoueces'],
    debounce((data?: { shapeId: string }) => {
      const colorIdIndex = editorStore.theme?.colorGroupTable.findIndex(
        (item) => item.id == editorStore.themeColorId
      );
      if ((colorIdIndex ?? -1) > 1) return;

      const shapeId = data?.shapeId;
      const shape = Shape.getShape(data?.shapeId ?? '');
      if (!shapeId || !shape) return;

      // 如果修改的是资源
      const colorIds =
        editorStore.theme?.colorGroupTable.map((item) => item.id) ?? [];
      const colorId = editorStore.themeColorId;

      const resources = cloneDeep(shape.attrs.resources ?? {});
      const resourcesStr = JSON.stringify(resources);

      const resourceNames = Object.keys(resources) as ResourceKey[];
      if (!resourceNames.length) return;
      resourceNames.forEach((resourceName) => {
        const defaultResouces = resources[resourceName]?.[colorId ?? ''] ?? [];
        if (!defaultResouces.length) return;
        resources[resourceName] = {
          ...resources[resourceName]
        };
        const _resouces = resources[resourceName];
        if (!_resouces) return;
        colorIds.forEach((_colorId) => {
          if (!_resouces[_colorId]?.length) {
            _resouces[_colorId] = [...defaultResouces];
          }
        });
      });

      // 这里先比对是否change，在处理
      if (resourcesStr != JSON.stringify(shape.attrs.resources ?? {}))
        shape.setAttrs({ resources });

      // 设置延时1000，不需要及时性
    }, 1000)
  );

  // 渲染完成后，将 Widget 的 runtime 尺寸同步回 shape.attrs.w/h
  // 解决属性面板 W/H 与画布渲染不一致的问题
  const _skipSyncReasons = new Set([
    'shape-transformed',
    'canvas-scale',
    'window-resize',
    'keep-data'
  ]);
  useEmitterListener(
    EventType['maml:rendered'],
    debounce((options?: { reason?: string }) => {
      if (_skipSyncReasons.has(options?.reason ?? '')) return;
      const theme = editorStore.theme;
      if (!theme) return;
      const runtimeStore = useRuntimeStore();
      const syncWidgetSize = (layers: StoreLayerItem[]) => {
        for (const layer of layers) {
          if (layer.type === ShapeType.Widget) {
            // 包含指针子元素的 Widget 跳过同步：指针的 runtime 尺寸含旋转区域，
            // 可能超出表盘分辨率，撑大 attrs.w/h 会导致导出校验报错
            const hasPointerChild = layer.children?.some(
              (child) =>
                child.type === ShapeType.Image && !!child.attrs?.source
            );
            if (!hasPointerChild) {
              const state = runtimeStore.shapeState(layer.id);
              if (state?.width && state?.height) {
                const rw = Math.round(state.width);
                const rh = Math.round(state.height);
                // 取 max：runtime 变大时跟着撑大，但不缩小到比成组时的包围盒更小
                // 避免 Widget.setSize 对 align 图组 offsetX 归零后 runtime < attrs 导致框缩小
                const finalW = Math.max(rw, layer.attrs.w ?? 0);
                const finalH = Math.max(rh, layer.attrs.h ?? 0);
                if (finalW !== layer.attrs.w || finalH !== layer.attrs.h) {
                  layer.attrs.w = finalW;
                  layer.attrs.h = finalH;
                  Shape.getShape(layer.id)?.initAttrs({ attrs: ['w', 'h'] });
                }
              }
            }
          }
          if (layer.children?.length) {
            syncWidgetSize(layer.children);
          }
        }
      };
      syncWidgetSize(theme.children);
    }, 200)
  );

  // 监听元素新增
  useEmitterListener(EventType['shape:create'], (id) => {
    if (layerStore.findLayer(id, editorStore.theme)) editorStore.select([id]);
  });

  // 监听元素删除
  useEmitterListener(
    EventType['shape:deleted'],
    (data?: { shapeId: string; parentId?: string }) => {
      const { shapeId, parentId = '' } = data ?? {};
      // 选择
      if (shapeId) editorStore.unselect(shapeId);

      const parent = Shape.getShape(parentId);

      // 如果删除的是widget的子元素
      if (parent?.type == ShapeType.Widget) {
        if (!parent.children.length) {
          parent.destroy();
        }
      }

      // 如果删除的是slot下的widget
      // 当slot内widget为空时，删除该slot
      if (parent?.type == ShapeType.Slot) {
        if (!parent.children.length) parent.destroy();
        // 如果删除的是要展示的元素，则增加一个默认展示
        if (shapeId == parent.attrs.widgetIdToDisplay) {
          parent?.setAttrs({
            widgetIdToDisplay: parent.children?.[0]?.id ?? ''
          });
        }
      }
    }
  );
  // 监听元素拖拽
  // useEmitterListener(
  //   EventType['shape:drag'],
  //   (data?: { shapeId: string; parentId?: string; preParentId?: string }) => {
  //     const { preParentId = '' } = data ?? {};

  //     // 确认移动之前的父元素
  //     const preParent = Shape.getShape(preParentId);
  //     if (preParent) {
  //       // 如果从父元素中拖拽的
  //       if ([ShapeType.Slot, ShapeType.Widget].includes(preParent.type)) {
  //         if (!preParent?.children?.length) {
  //           // preParent.destroy();
  //         }
  //       }
  //     }
  //   }
  // );

  watch(
    () => editorStore.selectedIds,
    (ids) => {
      if (ids.length) {
        editorStore.activeShapes = ids.map((id) => Shape.getShape(id));
      } else {
        editorStore.activeShapes = [];
      }
    }
  );

  // 当新的表盘打开时
  useEmitterListener(EventType['watchface:opened'], (path: string) => {
    // 更新时间
    if (path) {
      addItem(path);
      updateItem(path, { lastOpened: Date.now() });
    }
  });

  // 表盘打开后，加载需要的字体
  const { loadAllFonts } = useTextFont();
  useEmitterListener(EventType['watchface:opened'], () => {
    const path = ElectronApi.path;
    if (!path) return;
    // 遍历所有图层
    const fonts: { fontId: string; fontFile: string }[] = [];
    layerStore.themes.forEach((theme) => {
      theme.children.forEach((layer) => {
        const customFontFile = layer.attrs?.['customFontFile'] as string;
        if (!customFontFile) return;

        const fontFile = path.join(
          new WatchfaceService().ResourcePath,
          customFontFile
        );
        fonts.push({
          fontId: customFontFile.split('.')[0],
          fontFile
        });
      });
    });
    loadAllFonts(fonts);
  });

  // 监听元素拖拽
  useEmitterListener(EventType['shape:drag'], () => {
    // 判断rive是否在顶层或底层
    const theme = editorStore.theme;
    if (!theme) return;

    // 确保蒙层始终在最顶层
    const maskIndex = theme.children.findIndex(
      (item) => item.attrs._maskFlag === '_aod_global_mask'
    );
    if (maskIndex >= 0 && maskIndex < theme.children.length - 1) {
      const [mask] = theme.children.splice(maskIndex, 1);
      theme.children.push(mask);
      showToast('蒙层元素只能在顶部');
    }

    // 查找rive图层index
    const index = theme.children.findIndex(
      (item) => item.type == ShapeType.Rive
    );
    const rive = theme.children[index];
    if (!rive || index == 0 || index == theme.children.length - 1) return;
    Modal.confirm({
      title: '图层错误',
      content: 'rive图层只能在顶层或底层',
      okText: '移到顶部',
      cancelText: '移到底部',
      onOk() {
        theme.children.splice(index, 1);
        theme.children.push(rive);
        emitter.emit(EventType['shape:drag']);
      },
      onCancel() {
        theme.children.splice(index, 1);
        theme.children.unshift(rive);
        emitter.emit(EventType['shape:drag']);
      }
    });
  });

  // 导出缓存
  useImageBufferCache(true);

  // 表盘类型切换后
  useWatchfaceTypeChange();
};
